import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import {
    FileText,
    Timer,
    ListChecks,
    MessageSquareText,
    Lock,
    CheckCircle2,
    FileBadge,
} from "lucide-react";
import { MaskedLine } from "./Reveal";
import { BuyButton } from "./BuyButton";
import { PRICE_LABEL } from "@/lib/checkout";

const stats = [
    { icon: FileText, value: "3", label: "Simulazioni complete", testId: "hero-chip-simulazioni" },
    { icon: ListChecks, value: "120", label: "Quesiti totali", testId: "hero-chip-quesiti" },
    { icon: Timer, value: "60", label: "Minuti per test", testId: "hero-chip-tempo" },
    { icon: MessageSquareText, value: "✓", label: "Correzioni commentate", testId: "hero-chip-correzioni" },
];

const trust = [
    { icon: Lock, label: "Pagamento sicuro" },
    { icon: CheckCircle2, label: "Accesso immediato" },
];

export const BookCover = ({ small = false }) => (
    <div
        className={`relative h-full w-full overflow-hidden rounded-r-md bg-gradient-to-br from-[#0C2A52] to-[#071731] ${
            small ? "p-3" : "p-5 sm:p-7"
        }`}
    >
        <div className="absolute inset-y-0 left-0 w-1.5 bg-gold/80" />
        <div className={`relative flex h-full flex-col ${small ? "pl-2" : "pl-3"}`}>
            <p className={`font-display font-black leading-[0.95] text-white ${small ? "text-xs" : "text-2xl sm:text-3xl"}`}>
                DIFESA
                <br />
                <span className="text-gold">AMM 2026</span>
            </p>
            <p className={`mt-1 font-display font-bold tracking-wide text-slate-300 ${small ? "text-[6px]" : "text-[10px] sm:text-xs"}`}>
                ASSISTENTI AMMINISTRATIVI
            </p>
            <div className={`mt-3 rounded bg-gold/95 font-display font-black text-navy-950 ${small ? "p-1.5 text-[6px]" : "p-2.5 text-xs sm:text-sm"}`}>
                <span className={small ? "text-[9px]" : "text-lg sm:text-xl"}>3</span> SIMULAZIONI COMPLETE
                <span className={`block font-metric font-bold ${small ? "text-[5px]" : "text-[8px] sm:text-[10px]"}`}>
                    120 QUESITI CON CORREZIONI COMMENTATE
                </span>
            </div>
            {!small && (
                <ul className="mt-4 space-y-1.5 text-[10px] text-slate-300 sm:text-xs">
                    {["Quesiti di conoscenza", "Quesiti logico-deduttivi", "Quesiti situazionali", "Schede risposte", "Piano di ripasso finale"].map(
                        (t) => (
                            <li key={t} className="flex items-center gap-1.5">
                                <CheckCircle2 className="h-3 w-3 shrink-0 text-gold" />
                                {t}
                            </li>
                        ),
                    )}
                </ul>
            )}
            <div className={`mt-auto flex items-center gap-1.5 ${small ? "text-[5px]" : "text-[9px]"} font-metric tracking-widest text-slate-400`}>
                <FileBadge className={small ? "h-2 w-2" : "h-3 w-3"} />
                PDF DIGITALE · PRONTO DA SCARICARE
            </div>
        </div>
    </div>
);

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const glowY = useTransform(scrollYProgress, [0, 1], [0, 140]);
    const bookY = useTransform(scrollYProgress, [0, 1], [0, 70]);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
    const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-22, -10]), { stiffness: 120, damping: 18 });

    const onMove = (e) => {
        const r = e.currentTarget.getBoundingClientRect();
        mx.set((e.clientX - r.left) / r.width - 0.5);
        my.set((e.clientY - r.top) / r.height - 0.5);
    };

    return (
        <section
            ref={ref}
            className="grain relative overflow-hidden bg-[linear-gradient(135deg,#0C2A52_0%,#0A2142_55%,#071731_100%)] pt-24 text-white sm:pt-36"
        >
            <motion.div
                style={{ y: glowY }}
                className="animate-glow pointer-events-none absolute -top-32 right-[-10%] h-[480px] w-[620px] rounded-full bg-[#1D4E9E]/40 blur-[130px]"
            />
            <div className="pointer-events-none absolute bottom-0 left-[-10%] h-[320px] w-[520px] rounded-full bg-gold/10 blur-[120px]" />

            <div className="relative mx-auto grid max-w-6xl items-center gap-10 px-4 pb-14 sm:gap-12 sm:px-6 sm:pb-20 lg:grid-cols-[1.1fr_0.9fr]">
                <div>
                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.1, duration: 0.8 }}
                        className="font-metric text-[11px] tracking-[0.4em] text-slate-300"
                    >
                        CONCORSO PUBBLICO
                    </motion.p>

                    <h1
                        data-testid="hero-headline"
                        className="mt-3 font-display text-4xl font-black uppercase leading-[0.98] tracking-tight sm:mt-4 sm:text-6xl lg:text-7xl"
                    >
                        <MaskedLine delay={0.15}>Difesa</MaskedLine>
                        <MaskedLine delay={0.3}>
                            <span className="text-gold">AMM 2026</span>
                        </MaskedLine>
                        <MaskedLine delay={0.45}>
                            <span className="text-xl font-extrabold sm:text-4xl lg:text-5xl">3 simulazioni complete</span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.65, duration: 0.7 }}
                        className="mt-4 max-w-xl text-sm leading-relaxed text-slate-300 sm:mt-6 sm:text-base md:text-lg"
                    >
                        120 quesiti originali per esercitarti con struttura, tempi e criteri di punteggio della prova.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8, duration: 0.7 }}
                        className="mt-6 grid grid-cols-2 gap-3 sm:mt-8 sm:flex sm:items-center sm:gap-0 sm:divide-x sm:divide-white/15"
                    >
                        {stats.map((s) => (
                            <div key={s.testId} data-testid={s.testId} className="flex items-center gap-2.5 sm:gap-3 sm:px-5 sm:first:pl-0">
                                <s.icon className="h-5 w-5 shrink-0 text-gold sm:h-6 sm:w-6" />
                                <div>
                                    <p className="font-metric text-base font-bold leading-none text-white sm:text-lg">{s.value}</p>
                                    <p className="mt-1 text-[9px] uppercase tracking-wider text-slate-400 sm:text-[10px]">{s.label}</p>
                                </div>
                            </div>
                        ))}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.95, duration: 0.7 }}
                        className="mt-7 sm:mt-9"
                    >
                        <div className="flex flex-wrap items-center gap-4">
                            <div
                                data-testid="hero-price-badge"
                                className="rounded-lg bg-white px-6 py-3.5 font-metric text-2xl font-bold text-navy-950"
                            >
                                {PRICE_LABEL}
                            </div>
                            <BuyButton testId="hero-cta-download-btn" label="Ottieni le 3 simulazioni" />
                        </div>
                        <p className="mt-3 text-xs tracking-wide text-slate-400">Pagamento unico · Download digitale</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.15, duration: 0.8 }}
                        className="mt-5 flex flex-wrap gap-x-6 gap-y-2 sm:mt-7"
                    >
                        {trust.map((t) => (
                            <span key={t.label} className="inline-flex items-center gap-2 text-xs text-slate-300">
                                <t.icon className="h-4 w-4 text-gold" />
                                {t.label}
                            </span>
                        ))}
                    </motion.div>
                </div>

                <motion.div
                    style={{ y: bookY }}
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    className="relative mx-auto hidden w-full max-w-[300px] sm:block sm:max-w-[340px]"
                    onMouseMove={onMove}
                    onMouseLeave={() => {
                        mx.set(0);
                        my.set(0);
                    }}
                >
                    <div className="animate-glow absolute -inset-10 rounded-full bg-gold/15 blur-3xl" />
                    <div style={{ perspective: 1100 }}>
                        <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="book-float relative aspect-[3/4]">
                            <div className="gold-ring absolute inset-0 rounded-r-md">
                                <BookCover />
                            </div>
                            <div
                                className="absolute inset-y-0 left-0 w-[14px] rounded-l-sm bg-gradient-to-b from-[#123763] to-[#0A2142]"
                                style={{ transform: "rotateY(-84deg) translateX(-7px)", transformOrigin: "left center" }}
                            />
                            <div
                                className="absolute inset-y-[2px] right-0 w-[10px] bg-white/90"
                                style={{ transform: "rotateY(84deg) translateX(5px)", transformOrigin: "right center" }}
                            />
                        </motion.div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};
